import { useState } from "react";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { CreditCard, Lock, CheckCircle } from "lucide-react";

export function UpgradePopup({ toggle, isOpen, level }) {
  const [paymentMethod, setPaymentMethod] = useState("credit-card");

  const LEVEL_STYLES = {
    B1: {
      bgGradient: "from-purple-500 to-purple-600",
      bgLight: "bg-purple-50",
      text: "text-purple-600",
      textDark: "text-purple-800",
      textLight: "text-purple-700",
      border: "border-purple-200",
      button: "bg-purple-600 hover:bg-purple-700",
    },
    B2: {
      bgGradient: "from-orange-500 to-orange-600",
      bgLight: "bg-orange-50",
      text: "text-orange-600",
      textDark: "text-orange-800",
      textLight: "text-orange-700",
      border: "border-orange-200",
      button: "bg-orange-600 hover:bg-orange-700",
    },
    C1: {
      bgGradient: "from-pink-500 to-pink-600",
      bgLight: "bg-pink-50",
      text: "text-pink-600",
      textDark: "text-pink-800",
      textLight: "text-pink-700",
      border: "border-pink-200",
      button: "bg-pink-600 hover:bg-pink-700",
    },
  };

  const styles = LEVEL_STYLES[level];

  const handleUpgrade = () => {
    console.log("Upgrading with payment method:", paymentMethod);
    toggle();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-100 sm:max-w-lg bg-white mx-auto rounded-lg p-6 relative shadow-lg">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
          onClick={toggle}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x h-4 w-4"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span className="sr-only">Close</span>
        </button>
        <div>
          <h2 className={`text-2xl font-bold ${styles.text}`}>Upgrade to {level} Premium</h2>
          <div className="text-gray-600 text-sm mt-1">
            Unlock all {level} tests and practice for the 2025 Anglia Papers!
          </div>
        </div>
        <div className="space-y-6 py-6">
          <div className={`bg-gradient-to-br ${styles.bgGradient} p-6 rounded-lg text-white`}>
            <h3 className="text-xl font-semibold mb-3">{level} Premium Benefits:</h3>
            <ul className="space-y-2 pl-0 list-inside">
              {[
                "Access to all practice tests",
                "Exclusive 2025 Anglia Papers preparation",
                "Personalized progress tracking",
                "Advanced AI-powered feedback",
              ].map((benefit, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center">
            <span className={`text-4xl font-bold ${styles.text}`}>€16</span>
            <span className="text-gray-600 ml-2">One-time payment for all {level} tests</span>
          </div>
          <div className="space-y-3">
            <Label htmlFor="payment-method" className="text-lg font-semibold text-gray-700">
              Select Payment Method
            </Label>
            <RadioGroup
              id="payment-method"
              value={paymentMethod}
              onValueChange={(value) => setPaymentMethod(value)}
              className="space-y-3"
            >
              <div className={`flex items-center space-x-3 bg-gray-50 p-4 rounded-lg border ${styles.border}`}>
                <RadioGroupItem value="credit-card" id="credit-card" />
                <Label htmlFor="credit-card" className="flex items-center cursor-pointer">
                  <CreditCard className={`w-6 h-6 mr-3 ${styles.text}`} />
                  Credit Card
                </Label>
              </div>
              <div className={`flex items-center space-x-3 bg-gray-50 p-4 rounded-lg border ${styles.border}`}>
                <RadioGroupItem value="ideal" id="ideal" />
                <Label htmlFor="ideal" className="flex items-center cursor-pointer">
                  <img src="/ideal-logo.svg" alt="iDEAL" className="w-6 h-6 mr-3" />
                  iDEAL
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <Button onClick={handleUpgrade} className={`w-full ${styles.button} text-white text-lg py-6`}>
          <Lock className="w-5 h-5 mr-2" />
          Unlock All {level} Tests
        </Button>
      </div>
    </div>
  );
}
